import { useState, useEffect, useCallback } from 'react';

// ── Types ──

interface JobSummary {
  id: string;
  name: string;
  status: 'pending' | 'started' | 'success' | 'failure' | 'retry' | 'revoked';
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  queue: string;
  retries: number;
}

interface JobDetail extends JobSummary {
  args: unknown[];
  kwargs: Record<string, unknown>;
  result: unknown;
  traceback: string | null;
  max_retries: number;
  worker: string | null;
}

interface QueueStats {
  active: number;
  pending: number;
  failed: number;
  completed: number;
  total: number;
  workers_online: number;
}

type StatusFilter = 'all' | 'pending' | 'started' | 'success' | 'failure' | 'retry' | 'revoked';

// ── Helpers ──

const API_BASE = '/api/v1/jobs';

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: 'En attente', color: 'text-gray-400', bg: 'bg-gray-400/10' },
  started: { label: 'En cours', color: 'text-blue-400', bg: 'bg-blue-400/10' },
  success: { label: 'Termine', color: 'text-green-400', bg: 'bg-green-400/10' },
  failure: { label: 'Echoue', color: 'text-red-400', bg: 'bg-red-400/10' },
  retry: { label: 'Relance', color: 'text-amber-400', bg: 'bg-amber-400/10' },
  revoked: { label: 'Annule', color: 'text-purple-400', bg: 'bg-purple-400/10' },
};

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

// ── Component ──

export default function JobsPage() {
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedJob, setSelectedJob] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const pageSize = 20;

  const fetchJobs = useCallback(async () => {
    try {
      const statusParam = filter !== 'all' ? `&status=${filter}` : '';
      const data = await apiFetch<{ items: JobSummary[]; total: number }>(
        `${API_BASE}?page=${page}&page_size=${pageSize}${statusParam}`
      );
      setJobs(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    }
  }, [filter, page]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiFetch<QueueStats>(`${API_BASE}/stats`);
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchJobs(), fetchStats()]);
    setLoading(false);
  }, [fetchJobs, fetchStats]);

  // Initial load + auto-refresh every 5s
  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 5000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  // ── Actions ──

  const handleRetry = async (jobId: string) => {
    try {
      await apiFetch(`${API_BASE}/${jobId}/retry`, { method: 'POST' });
      await fetchAll();
    } catch (err) {
      console.error('Failed to retry job:', err);
    }
  };

  const handleCancel = async (jobId: string) => {
    try {
      await apiFetch(`${API_BASE}/${jobId}`, { method: 'DELETE' });
      await fetchAll();
    } catch (err) {
      console.error('Failed to cancel job:', err);
    }
  };

  const openDetail = async (jobId: string) => {
    try {
      const detail = await apiFetch<JobDetail>(`${API_BASE}/${jobId}`);
      setSelectedJob(detail);
    } catch (err) {
      console.error('Failed to fetch job detail:', err);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-100">File de taches</h1>
        <p className="text-sm text-gray-500 mt-1">Surveillance et gestion des taches en arriere-plan</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Actives" value={stats?.active ?? 0} color="blue" />
        <StatCard label="En attente" value={stats?.pending ?? 0} color="gray" />
        <StatCard label="Echouees" value={stats?.failed ?? 0} color="red" />
        <StatCard label="Terminees" value={stats?.completed ?? 0} color="green" />
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', 'pending', 'started', 'success', 'failure', 'retry', 'revoked'] as StatusFilter[]).map((s) => (
          <button
            key={s}
            onClick={() => { setFilter(s); setPage(1); }}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              filter === s
                ? 'bg-cyber-cyan/20 text-cyber-cyan border-cyber-cyan/30'
                : 'text-gray-400 border-border-subtle hover:text-gray-200 hover:bg-surface-tertiary'
            }`}
          >
            {s === 'all' ? 'Tout' : STATUS_CONFIG[s]?.label ?? s}
          </button>
        ))}

        {/* Workers online indicator */}
        {stats && (
          <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
            <div className={`w-2 h-2 rounded-full ${stats.workers_online > 0 ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
            {stats.workers_online} worker{stats.workers_online !== 1 ? 's' : ''} en ligne
          </div>
        )}
      </div>

      {/* Job Table */}
      <div className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-subtle">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Cree le</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Duree</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && jobs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    Chargement...
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    Aucune tache trouvee
                  </td>
                </tr>
              ) : (
                jobs.map((job) => {
                  const cfg = STATUS_CONFIG[job.status] ?? STATUS_CONFIG.pending;
                  return (
                    <tr
                      key={job.id}
                      className="border-b border-border-subtle/50 hover:bg-surface-tertiary/30 cursor-pointer transition-colors"
                      onClick={() => openDetail(job.id)}
                    >
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-400">{job.id.slice(0, 8)}...</span>
                      </td>
                      <td className="px-4 py-3 text-gray-200">{job.name}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${cfg.color} ${cfg.bg}`}>
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{formatDate(job.created_at)}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs font-mono">{formatDuration(job.duration_seconds)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {(job.status === 'failure' || job.status === 'revoked') && (
                            <button
                              onClick={() => handleRetry(job.id)}
                              className="px-2 py-1 text-[10px] font-medium rounded bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors"
                              title="Relancer"
                            >
                              Relancer
                            </button>
                          )}
                          {(job.status === 'pending' || job.status === 'started') && (
                            <button
                              onClick={() => handleCancel(job.id)}
                              className="px-2 py-1 text-[10px] font-medium rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                              title="Annuler"
                            >
                              Annuler
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border-subtle">
            <span className="text-xs text-gray-500">
              {total} tache{total !== 1 ? 's' : ''} au total
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="px-2 py-1 text-xs rounded border border-border-subtle text-gray-400 hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Precedent
              </button>
              <span className="px-2 text-xs text-gray-400">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-2 py-1 text-xs rounded border border-border-subtle text-gray-400 hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Job Detail Modal */}
      {selectedJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setSelectedJob(null)}>
          <div
            className="bg-surface-secondary border border-border-subtle rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-border-subtle">
              <div>
                <h2 className="text-lg font-semibold text-gray-100">Detail de la tache</h2>
                <p className="text-xs text-gray-500 font-mono mt-0.5">{selectedJob.id}</p>
              </div>
              <button
                onClick={() => setSelectedJob(null)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-surface-tertiary transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              {/* Status + Meta */}
              <div className="grid grid-cols-2 gap-4">
                <DetailField label="Nom" value={selectedJob.name} />
                <DetailField
                  label="Statut"
                  value={
                    <span className={`${STATUS_CONFIG[selectedJob.status]?.color ?? 'text-gray-400'} font-semibold`}>
                      {STATUS_CONFIG[selectedJob.status]?.label ?? selectedJob.status}
                    </span>
                  }
                />
                <DetailField label="Queue" value={selectedJob.queue} />
                <DetailField label="Worker" value={selectedJob.worker ?? '-'} />
                <DetailField label="Cree le" value={formatDate(selectedJob.created_at)} />
                <DetailField label="Demarre le" value={formatDate(selectedJob.started_at)} />
                <DetailField label="Termine le" value={formatDate(selectedJob.completed_at)} />
                <DetailField label="Duree" value={formatDuration(selectedJob.duration_seconds)} />
                <DetailField label="Tentatives" value={`${selectedJob.retries} / ${selectedJob.max_retries}`} />
              </div>

              {/* Result */}
              {selectedJob.result !== null && selectedJob.result !== undefined && (
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Resultat</h3>
                  <pre className="bg-surface-primary rounded-lg p-3 text-xs text-gray-300 overflow-x-auto max-h-40 border border-border-subtle">
                    {typeof selectedJob.result === 'string'
                      ? selectedJob.result
                      : JSON.stringify(selectedJob.result, null, 2)}
                  </pre>
                </div>
              )}

              {/* Traceback */}
              {selectedJob.traceback && (
                <div>
                  <h3 className="text-xs font-medium text-red-400 uppercase mb-1">Traceback</h3>
                  <pre className="bg-red-950/30 border border-red-500/20 rounded-lg p-3 text-xs text-red-300 overflow-x-auto max-h-60 whitespace-pre-wrap">
                    {selectedJob.traceback}
                  </pre>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                {(selectedJob.status === 'failure' || selectedJob.status === 'revoked') && (
                  <button
                    onClick={() => {
                      handleRetry(selectedJob.id);
                      setSelectedJob(null);
                    }}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20 transition-colors"
                  >
                    Relancer cette tache
                  </button>
                )}
                {(selectedJob.status === 'pending' || selectedJob.status === 'started') && (
                  <button
                    onClick={() => {
                      handleCancel(selectedJob.id);
                      setSelectedJob(null);
                    }}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-colors"
                  >
                    Annuler cette tache
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colorMap: Record<string, string> = {
    blue: 'text-blue-400 bg-blue-400/10',
    gray: 'text-gray-400 bg-gray-400/10',
    red: 'text-red-400 bg-red-400/10',
    green: 'text-green-400 bg-green-400/10',
  };

  return (
    <div className="stat-card">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
      <p className={`mt-2 text-2xl font-bold font-mono ${colorMap[color]?.split(' ')[0] ?? 'text-gray-100'}`}>
        {value.toLocaleString()}
      </p>
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-medium text-gray-500 uppercase">{label}</p>
      <p className="text-sm text-gray-200 mt-0.5">{value}</p>
    </div>
  );
}
